import React, { useContext } from "react";
import Navbar from "components/navigation/web/Navbar";
import { useTranslation } from "react-i18next";
import {
	Box,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	IconButton,
	Grid,
	Menu,
	MenuItem,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { getBorder } from "utils/Styles";
import { gettingSubPage } from "utils/CustomReports";
import { useNavigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { GlobalContext } from "GlobalContext";
/* ICONS */
import DifferenceIcon from "@mui/icons-material/Difference";
import CategoryIcon from "@mui/icons-material/Category";
import ArticleIcon from "@mui/icons-material/Article";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { jwtDecode } from "jwt-decode";
import { ContentBreadcrumbs } from 'components/navigation/web/ContentBreadcrumbs'
import { buildSubpagePath, getSubpageFromLocation } from 'utils/subpageRouting'

// Define drawer width constants
const drawerWidth = 240;

// Mixins para estados abierto y cerrado, basados en el ejemplo
const openedMixin = (theme) => ({
	width: drawerWidth,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
	backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up("sm")]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
		backgroundColor: theme.palette.background.paper,
	},
});

// Usa un componente Box estilizado en lugar de un Drawer
const SideMenu = styled(Box, { shouldForwardProp: (prop) => prop !== "open" })(
	({ theme, open }) => ({
		height: `calc(100vh - 64px)`,
		borderRight: getBorder(theme.palette.mode),
		whiteSpace: "nowrap",
		boxSizing: "border-box",
		...(open && {
			...openedMixin(theme),
			backgroundColor: theme.palette.background.paper,
		}),
		...(!open && {
			...closedMixin(theme),
			backgroundColor: theme.palette.background.paper,
		}),
	})
);

const gettingAllowViews = () => {
	if (localStorage.getItem("access_token")) {
		return jwtDecode(localStorage.getItem("access_token"))?.views;
	} else {
		return [];
	}
};

const useMenuItems = (showResume = true) => {
	const { t } = useTranslation();

	const menuItems = [
		{
			fullpath: "/custom_reports?subpage=resume",
			routePath: "/custom_reports/resume",
			path: "resume",
			icon: <ArticleIcon />,
			label: t("custom_reports.title"),
			order: 1,
		},
		{
			fullpath: "/custom_reports?subpage=report_configurations",
			routePath: "/custom_reports/report_configurations",
			path: "report_configurations",
			icon: <SettingsApplicationsIcon />,
			label: t("custom_reports.submenu.report_configurations"),
		},
		{
			fullpath: "/custom_reports?subpage=category",
			routePath: "/custom_reports/category",
			path: "category",
			icon: <CategoryIcon />,
			label: t("custom_reports.submenu.category"),
		},
		{
			fullpath: "/custom_reports?subpage=template",
			routePath: "/custom_reports/template",
			path: "template",
			icon: <DifferenceIcon />,
			label: t("custom_reports.submenu.template"),
		},
		{
			fullpath: "/custom_reports?subpage=execute_reports",
			routePath: "/custom_reports/execute_reports",
			path: "execute_reports",
			icon: <AutoAwesomeMotionIcon />,
			label: t("custom_reports.submenu.execute_reports"),
		},
	];

	const visibleMenuItems = showResume ? menuItems : menuItems.filter((item) => item.path !== "resume");

	const allowedViews = visibleMenuItems.filter((item) =>
		gettingAllowViews().includes(item.fullpath)
	);

	const sortedMenuItems = allowedViews.sort((a, b) => {
		const orderA = a.order !== undefined ? a.order : Infinity;
		const orderB = b.order !== undefined ? b.order : Infinity;
		return orderA - orderB || a.label.localeCompare(b.label);
	});

	return sortedMenuItems;
};

const Component = () => {
	const theme = useTheme();
	const { hide, openLeft } = useContext(GlobalContext);
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = React.useState(() => {
		const stored = localStorage.getItem("web_submenu_open");
		return stored === null ? !hide : stored === "true";
	});

	const navigate = useNavigate();
	const location = useLocation();

	const menuItems = useMenuItems(!openLeft);
	const { subpage: paramsSubpage } = useParams();
	const [searchParams] = useSearchParams();
	const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams)
	const selectedItem = menuItems.find((item) => item.path === selectedSubpage)

	const toggleDrawer = () => {
		const nextOpen = !isOpen;
		localStorage.setItem("web_submenu_open", String(nextOpen));
		setIsOpen(nextOpen);
	};

	const quickSwitchMenus = [
		{ path: "/settings/resume", label: t("settings.title"), match: "/settings" },
		{ path: "/timekeeper/resume", label: t("timekeeper.title"), match: "/timekeeper" },
		{ path: "/user_management/resume", label: t("user_management.title"), match: "/user_management" },
		{ path: "/custom_reports/resume", label: t("custom_reports.title"), match: "/custom_reports" },
		{ path: "/accruals/resume", label: t("accruals.title"), match: "/accruals" },
		{ path: "/schedule-rules/resume", label: t("schedule_rules.title"), match: "/schedule-rules" },
	];

	const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

	const handleOpenQuickSwitch = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
	};

	const handleHoverOpenQuickSwitch = (event) => {
		if (isOpen) return;
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseQuickSwitch = () => {
		setMenuAnchorEl(null);
	};

	const handleQuickSwitch = (path) => {
		handleCloseQuickSwitch();
		navigate(path);
	};

	return (
		<Box
			display={"flex"}
			sx={{
				padding: 0,
				margin: 0,
			}}
		>
			<SideMenu open={isOpen} sx={{ minWidth: isOpen ? 240 : undefined }}>
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
					<List sx={{ flexGrow: 1, overflowY: "auto" }}>
						{menuItems.map((item) => (
							<Tooltip
								key={item.path}
								arrow
								disableInteractive
								title={!isOpen ? (item.path === "resume" ? "" : item.label) : ""}
								placement="right"
							>
								<ListItemButton
									sx={{
										minHeight: "auto",
										height: "auto",
										px: 2.5,
										justifyContent: isOpen
											? "initial"
											: "center",
										alignItems: "center",
									}}
									selected={
										selectedSubpage === item.path
									}
									onClick={() => navigate(item.routePath || buildSubpagePath('/custom_reports', item.path))}
									onMouseEnter={item.path === "resume" ? handleHoverOpenQuickSwitch : undefined}
								>
									<ListItemIcon
										sx={{
											minWidth: 0,
											mr: isOpen ? 3 : "auto",
											justifyContent: "center",
											alignItems: "center",
											height: 24,
										}}
									>
										{item.icon}
									</ListItemIcon>
									<ListItemText
										primary={item.label}
										sx={{
											opacity: isOpen ? 1 : 0,
											padding: 0,
										}}
									/>
									{item.path === "resume" && (
										<IconButton
											size="small"
											onClick={handleOpenQuickSwitch}
											sx={{ ml: "auto", display: isOpen ? "inline-flex" : "none" }}
										>
											<ChevronRightIcon fontSize="small" />
										</IconButton>
									)}
								</ListItemButton>
							</Tooltip>
						))}
					</List>
					<Menu
						anchorEl={menuAnchorEl}
						open={Boolean(menuAnchorEl)}
						onClose={handleCloseQuickSwitch}
						anchorOrigin={{ vertical: "top", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "left" }}
						MenuListProps={{ onMouseLeave: handleCloseQuickSwitch }}
					>
						{quickSwitchMenus.map((menu) => (
							<MenuItem key={menu.path} onClick={() => handleQuickSwitch(menu.path)}>
								{menu.label}
							</MenuItem>
						))}
					</Menu>
					<Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: isOpen ? "flex-end" : "center", p: 1 }}>
						<IconButton onClick={toggleDrawer}>
							{isOpen ? <FirstPageIcon /> : <LastPageIcon />}
						</IconButton>
					</Box>
				</Box>
			</SideMenu>
			<Grid
				container
				sx={{
					flexGrow: 1,
					px: 2,
					pt: 1,
					pb: 1,
					transition: theme.transitions.create("margin", {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
					...(isOpen && {
						transition: theme.transitions.create("margin", {
							easing: theme.transitions.easing.easeOut,
							duration: theme.transitions.duration.enteringScreen,
						}),
					}),
				}}
			>
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
					sx={{
						flexGrow: 1,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						overflowX: "hidden",
						overflowY: "hidden",
					}}
				>
					{selectedItem && selectedItem.path !== 'resume' && (
						<ContentBreadcrumbs
							items={[
								{ label: t('home.title'), to: '/' },
								{ label: selectedItem.label }
							]}
						/>
					)}
					{gettingSubPage(
						selectedSubpage,
						false,
						location
					)}
				</Grid>
			</Grid>
		</Box>
	);
};

const CustomReports = () => {
	const { t } = useTranslation();
	const { openLeft } = useContext(GlobalContext);
	const { subpage: paramsSubpage } = useParams();
	const [searchParams] = useSearchParams();
	const menuItems = useMenuItems(!openLeft);
	const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams)

	return (
		<Navbar
			view={{
				title:
					t("custom_reports.title") +
					" - " +
					menuItems.find(
							(item) => item.path === selectedSubpage
					)?.label,
				component: <Component />,
			}}
			right={{
				title: "Right",
				component: <> componente </>,
			}}
		/>
	);
};

export { CustomReports, useMenuItems };
